import { memo, useMemo, useState } from "react";
import { animated, useSpring } from "@react-spring/web";
import styled from "styled-components/macro";
import { lightGrey } from "constants/colors";
import { useSettings } from "contexts/SettingsManager/SettingsManager";
import {
  GROUP_METRIC_MARGIN_HZ,
  GROUP_METRIC_WIDTH,
  GROUP_METRIC_HEIGHT,
} from "./groupMetricsUtils";
import {
  BREAKPOINT_DESKTOP_XL,
  BREAKPOINT_TABLET,
  useBreakpoints,
  useMount,
} from "constants/constants";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { useIsDarkMode } from "store/atoms";
import {
  GROUP_METRICS_FLOATER_HEIGHT,
  GROUP_METRICS_PADDING_HZ,
} from "./GroupMetricsStyles";
import { startCase } from "lodash";
import { useMetrics } from "./useMetrics";
import { useVisibleMetrics } from "./useVisibleMetrics";

const PADDING_HZ = 18;
const CHARS_THAT_FIT_IN_FLOATER = 4; // * not a perfect strategy (only works for monospace fonts)

/** grey floater that indicates which metric is selected as preferredMetric */
export const GroupMetricsFloater = memo(({ scale }: { scale: number }) => {
  const { smMdLg } = useBreakpoints();
  const groupMetricWidth: number = useMemo(
    () => GROUP_METRIC_WIDTH[smMdLg],
    [smMdLg]
  );
  const preferredMetricTitle = usePreferredMetricTitle();

  const { extraWidth, extraHeight } = useMemo(() => {
    // use the longest word to determine the width
    const words = preferredMetricTitle.split(" ");
    const longestWord = words.reduce((a, b) => (a.length > b.length ? a : b));
    // add some width if the title is long e.g. "Availability"
    const extraChars = longestWord?.length - CHARS_THAT_FIT_IN_FLOATER;
    const extraWidth = extraChars * 5;
    const shouldAddExtraHeight =
      words.length > 1 && preferredMetricTitle.length > 7;
    const extraHeight = shouldAddExtraHeight ? 12 : 0;
    return { extraWidth, extraHeight };
  }, [preferredMetricTitle]);
  // don't animate on first mount
  const [immediate, setImmediate] = useState(true);
  useMount(() => {
    window.setTimeout(() => {
      setImmediate(false);
    });
  });

  const springFloater = useSpringFloaterPosition(
    groupMetricWidth,
    immediate,
    scale
  );
  useSelectFirstValidMetricIfInvalid();

  const [isDarkMode] = useIsDarkMode();

  return (
    <AnimatedPreferredMetricFloater
      className={`preferredMetricFloater${extraHeight ? " extraHeight" : ""}`}
      style={springFloater}
      $groupMetricWidth={groupMetricWidth - 2 * PADDING_HZ + extraWidth}
      $extraWidth={extraWidth}
      $extraHeight={extraHeight}
      $immediate={immediate}
      $isDarkMode={isDarkMode}
      $scale={scale}
    />
  );
});
GroupMetricsFloater.displayName = "GroupMetricsFloater";

function usePreferredMetricTitle() {
  const { metrics } = useMetrics();
  const { preferredMetric } = useSettings();
  const { translate } = useFlightTranslation();
  return useMemo(() => {
    const preferredMetricDisplayName = metrics.find(
      d => d.dataDumpKey === preferredMetric
    )?.displayName;
    const titleCasePreferredMetric = startCase(preferredMetricDisplayName);
    const preferredMetricTitle = translate(titleCasePreferredMetric);
    return preferredMetricTitle;
  }, [metrics, preferredMetric, translate]);
}

const AnimatedPreferredMetricFloater = styled(animated.div)<{
  $groupMetricWidth: number;
  $extraWidth: number;
  $extraHeight: number;
  $immediate: boolean;
  $isDarkMode: boolean;
  $scale: number;
}>`
  position: absolute;
  left: ${-PADDING_HZ - 4}px;
  left: ${props => -props.$extraWidth / 2}px;
  @media (min-width: ${BREAKPOINT_TABLET}px) {
    left: ${props => -props.$extraWidth / 2}px;
  }
  top: ${props => -6 - props.$extraHeight / 2}px;
  background: ${({ $isDarkMode }) => ($isDarkMode ? "#474747" : lightGrey)};
  width: ${props => props.$groupMetricWidth + 2 * PADDING_HZ}px;
  transition: ${props =>
    props.$immediate
      ? "none"
      : "width 0.3s ease-in-out, height 0.3s ease-in-out, top 0.3s ease-in-out"};
  height: ${props => GROUP_METRICS_FLOATER_HEIGHT + props.$extraHeight}px;
  border-radius: 5px;
  @media (min-width: ${BREAKPOINT_DESKTOP_XL}) {
    left: ${props => (props.$scale * -props.$extraWidth) / 2}px;
    width: ${props =>
      props.$scale * (props.$groupMetricWidth + 2 * PADDING_HZ)}px;
    height: ${props => props.$scale * (GROUP_METRIC_HEIGHT + 12)}px;
    top: ${props => props.$scale * (-6 - props.$extraHeight / 2)}px;
    border-radius: ${props => props.$scale * 5}px;
  }
`;

/** move the floater behind the preferredMetric */
function useSpringFloaterPosition(
  groupMetricWidth: number,
  immediate: boolean,
  scale: number
) {
  const { preferredMetric } = useSettings();
  const visibleMetrics = useVisibleMetrics();
  const floaterLeft = useMemo(() => {
    const preferredMetricIdx = visibleMetrics.GroupMetrics.findIndex(
      metric => metric.dataDumpKey === preferredMetric
    );
    const floaterLeft =
      GROUP_METRIC_MARGIN_HZ +
      preferredMetricIdx * (groupMetricWidth + 2 * GROUP_METRIC_MARGIN_HZ) +
      GROUP_METRICS_PADDING_HZ;
    return floaterLeft;
  }, [groupMetricWidth, preferredMetric, visibleMetrics.GroupMetrics]);

  return useSpring({
    transform: `translateX(${floaterLeft * scale}px)`,
    immediate,
  });
}

/** preferredMetric starts as `uptimePercent` or the previous value from previous customer (localStorage), but not all customer have `uptimePercent` metric, so if visibleMetrics.GroupMetrics doesn't contain 'uptimePercent', set preferredMetric to the first visibleMetric.GroupMetrics */
function useSelectFirstValidMetricIfInvalid() {
  const { preferredMetric, setPreferredMetric } = useSettings();
  const visibleMetrics = useVisibleMetrics();

  useMount(() => {
    const preferredMetricIdx = visibleMetrics.GroupMetrics.findIndex(
      metric => metric.dataDumpKey === preferredMetric
    );
    const notFound = preferredMetricIdx === -1;
    const firstVisibleMetric = visibleMetrics.GroupMetrics[0]?.dataDumpKey;
    if (notFound && firstVisibleMetric) {
      setPreferredMetric(firstVisibleMetric);
    }
  });
}
